import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { Reveal } from "../components/site/Reveal";
import { api, trackEvent } from "../lib/api";
import { useTitle } from "../App";

const OBJECTIVES = ["All", "Awareness", "Lead generation", "Sales", "App growth", "Retention"];

export default function Solutions() {
  useTitle("Marketing & Growth");
  const [solutions, setSolutions] = useState(null);
  const [objective, setObjective] = useState("All");

  useEffect(() => {
    api.get("/content/solutions").then((r) => setSolutions(r.data)).catch(() => setSolutions([]));
  }, []);

  const list = (solutions || []).filter((s) => objective === "All" || (s.objectives || []).includes(objective));

  return (
    <div data-testid="solutions-page" className="bg-midnight text-mist">
      <Navbar />
      <section className="grain pt-[76px]">
        <div className="container-ek py-24 sm:py-28">
          <Reveal>
            <p className="eyebrow mb-4">Marketing & Growth</p>
            <h1 className="max-w-3xl font-display text-4xl font-bold tracking-tight sm:text-6xl">A clearer plan. Stronger creative. Better decisions.</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Marketing begins with the brand's offering, audience, business objective and economics. These solution areas describe how Ekavrix works — filter by what you are trying to achieve.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <span className="mr-1 font-mono text-[11px] uppercase tracking-widest text-slate-500">Objective:</span>
              {OBJECTIVES.map((o) => (
                <button key={o} data-testid={`objective-${o.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setObjective(o)}
                  className={`rounded-full border px-4 py-2 font-display text-sm font-medium transition-[background-color,border-color,color] duration-300 ${objective === o ? "border-teal bg-teal text-midnight" : "border-white/12 text-slate-300 hover:border-teal/50 hover:text-white"}`}>
                  {o}
                </button>
              ))}
            </div>
            <p className="mt-4 max-w-xl text-xs leading-relaxed text-slate-500">
              Objectives illustrate suitable approaches; not every channel suits every brand, and we will say so when it does not.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="container-ek">
          {solutions === null ? (
            <p className="py-16 text-center text-slate-500">Loading solutions...</p>
          ) : list.length === 0 ? (
            <p data-testid="solutions-empty" className="py-16 text-center text-slate-500">No solution areas match this objective yet — ask us directly.</p>
          ) : (
            <div className="space-y-4">
              {list.map((s, i) => (
                <Reveal key={s.slug} delay={i * 0.05}>
                  <Link to={`/solutions/${s.slug}`} data-testid={`solution-${s.slug}`} onClick={() => trackEvent("solution_interest", { solution: s.slug })}
                    className="card-dark group grid items-center gap-6 p-7 sm:p-9 lg:grid-cols-12">
                    <div className="lg:col-span-1">
                      <span className="font-mono text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="lg:col-span-7">
                      <h2 className="font-display text-xl font-semibold transition-colors group-hover:text-violet sm:text-2xl">{s.title}</h2>
                      <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-400">{s.summary}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 lg:col-span-3">
                      {(s.objectives || []).map((o) => (
                        <span key={o} className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">{o}</span>
                      ))}
                    </div>
                    <div className="lg:col-span-1 lg:text-right">
                      <ArrowUpRight size={20} className="text-teal transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
          <Reveal delay={0.1} className="mt-14 text-center">
            <Link to="/contact?category=grow-my-brand" data-testid="solutions-cta-bottom" className="btn-primary">
              Discuss My Growth Plan <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
}
